import { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Tr } from '@utils';
import { FleetManagerClearDataDto } from '@services';
import { Box, Grid, Stack } from '@mui/material';
import { VoltaageModalDialog, IVoltaageModalDialogProps, FormikSubmitButton, FormikCheckbox } from '@components/common';

enum ClearDataType {
	CAR_LIST_BANDS = 'carListBands',
	LOCATIONS = 'locations',
	FLEET_VEHICLES = 'fleetVehicles'
}

type FormFields = Extendable<{
	selections: ClearDataType[];
}>;

const getInitialValues = (): FormFields => ({
	selections: [ClearDataType.FLEET_VEHICLES]
});

export interface IFleetManagerClearDataDialogProps extends Omit<IVoltaageModalDialogProps, 'onSubmit'> {
	onSubmit?: (entity: Partial<FleetManagerClearDataDto>) => void;
}

export const FleetManagerClearDataDialog: FC<IFleetManagerClearDataDialogProps> = ({ onSubmit, ...props }) => {
	const { open } = props;
	const formik = useFormik<FormFields>({
		initialValues: getInitialValues(),
		validateOnChange: true,
		validationSchema: yup.object({
			selections: yup.array()
		}),
		onSubmit: ({ selections }, api) => {
			api.setSubmitting(false);
			onSubmit?.({
				deleteDriverCategory: selections.includes(ClearDataType.CAR_LIST_BANDS),
				deleteLocation: selections.includes(ClearDataType.LOCATIONS),
				deleteVehicleList: selections.includes(ClearDataType.FLEET_VEHICLES)
			});
		}
	});

	useEffect(() => {
		open && formik.resetForm({ errors: {}, values: getInitialValues() });
	}, [open]);

	return (
		<VoltaageModalDialog
			title={<Tr.Portal path="configuration.fleet-manager.clear-data.modal-title" />}
			dividers
			maxWidth="sm"
			{...props}
		>
			<Box component="form" onSubmit={formik.handleSubmit} sx={{ flexDirection: 'column', paddingBottom: 8 }}>
				<Grid container rowSpacing={1} sx={{ paddingX: 14, paddingTop: 8 }}>
					<Grid item xs={12}>
						<FormikCheckbox
							formikApi={formik}
							name="selections"
							value={ClearDataType.CAR_LIST_BANDS}
							formControlLabelProps={{
								label: <Tr.Portal path="configuration.fleet-manager.clear-data.car-list-bands" />,
								sx: { marginRight: 0 }
							}}
						/>
					</Grid>
					<Grid item xs={12}>
						<FormikCheckbox
							formikApi={formik}
							name="selections"
							value={ClearDataType.LOCATIONS}
							formControlLabelProps={{
								label: <Tr.Portal path="configuration.fleet-manager.clear-data.locations" />,
								sx: { marginRight: 0 }
							}}
						/>
					</Grid>
					<Grid item xs={12}>
						<FormikCheckbox
							formikApi={formik}
							name="selections"
							value={ClearDataType.FLEET_VEHICLES}
							disabled
							formControlLabelProps={{
								label: <Tr.Portal path="configuration.fleet-manager.clear-data.fleet-vehicles" />,
								sx: { marginRight: 0 }
							}}
						/>
					</Grid>
				</Grid>
				<Stack sx={{ paddingTop: 5, justifyContent: 'center' }}>
					<FormikSubmitButton formik={formik} color="error" data-testid="fleet-manager-clear-data-form-submit-button">
						<Tr.Portal path="configuration.fleet-manager.clear-data.clear-data-button" />
					</FormikSubmitButton>
				</Stack>
			</Box>
		</VoltaageModalDialog>
	);
};
